import { Component, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Observable, forkJoin } from 'rxjs';
import { AgentRefreshService } from '../../agent/agent-refresh.service';
import { ConfirmDialogComponent } from '../../components/dialogs/confirm-dialog/confirm-dialog.component';
import { GuardrailHitsDialogComponent } from '../../components/dialogs/guardrail-hits-dialog/guardrail-hits-dialog.component';
import { GuardrailRuleDialogComponent } from '../../components/dialogs/guardrail-rule-dialog/guardrail-rule-dialog.component';
import { AlTableBadgeComponent } from '../../components/features/table-badge/table-badge.component';
import { AlTableTextComponent } from '../../components/features/table-text/table-text.component';
import {
  AlTableColumn,
  AlTableRowAction,
} from '../../components/features/table/table.component';
import {
  GuardrailRule,
  GuardrailRuleInput,
  GuardrailService,
} from '../../services/guardrail.service';

/**
 * Guardrail rules — serves BOTH scopes from one component.
 *
 *   /guardrails                     organisation-wide rules
 *   /tutors/:tutorId/guardrails     this bot's rules + the inherited
 *                                   organisation rules, shown read-only
 *
 * Which one is active follows from the route: a `tutorId` in the parent
 * params switches the page into bot scope. The two views differ only in copy
 * and in whether a row is editable, so a second component would have been a
 * copy of this one.
 *
 * Rendering goes through the shared `app-table` (sorting, paging, empty row,
 * per-row action menu with disabled state) — the hand-rolled table this page
 * started with had overlapping columns and duplicated all of that.
 */
@Component({
  selector: 'app-guardrails',
  templateUrl: './guardrails.component.html',
  styleUrl: './guardrails.component.scss',
})
export class GuardrailsComponent implements OnInit {
  rules: GuardrailRule[] = [];
  isLoading = false;

  /** Rendered as a tab of the agent-setup pages (no own header/card). */
  @Input() embedded = false;

  /** Set when the page runs under /tutors/:tutorId/guardrails. */
  tutorId: string | null = null;

  /** Server-side total. Larger than the loaded array means the fetch limit
   *  bit — the page says so rather than quietly showing a short list. */
  total = 0;

  columns: AlTableColumn[] = [];
  rowActions: AlTableRowAction[] = [];
  globalActions: { key: string; title: string; icon: string }[] = [];

  readonly pageSize = 25;

  constructor(
    private guardrailService: GuardrailService,
    private dialogService: NbDialogService,
    private toastrService: NbToastrService,
    private route: ActivatedRoute,
    private t: TranslateService
  ) {
    // The in-house agent changed a guardrail — edits here happen in dialogs, so reloading loses nothing.
    inject(AgentRefreshService)
      .on('guardrails')
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.load());
  }

  ngOnInit(): void {
    // `tutorId` lives on the parent route (`tutors/:tutorId`), this page is
    // one of its children.
    this.tutorId =
      this.route.snapshot.paramMap.get('tutorId') ??
      this.route.parent?.snapshot.paramMap.get('tutorId') ??
      null;

    this.buildTable();
    this.load();
  }

  get scope(): 'client' | 'bot' {
    return this.tutorId ? 'bot' : 'client';
  }

  /** Organisation rules shown on a bot page: visible for context, but not
   *  editable there — a bot owner must not be able to weaken the client
   *  admin's policy. */
  isInherited(rule: GuardrailRule): boolean {
    return this.scope === 'bot' && !rule.bot_id;
  }

  get rulesTruncated(): boolean {
    return this.total > this.rules.length;
  }

  // ── Search ──
  //
  // Same shape as the other tables (files / courses / website): the page owns
  // `searchText` and hands the filtered array to `app-table`, which has no
  // search of its own.

  searchText = '';

  get filteredRules(): GuardrailRule[] {
    const q = this.searchText.trim().toLowerCase();
    if (!q) return this.rules;
    return this.rules.filter((r) =>
      [r.name, r.definition, ...(r.examples || [])]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q))
    );
  }

  onSearchChange(): void {
    // No-op beyond triggering change detection — `filteredRules` recomputes.
    // Kept explicit so the template reads like the other search pages.
  }

  // ── Table definition ──

  private buildTable(): void {
    const inheritedLabel = this.t.instant('pages.guardrails.inherited');

    this.columns = [
      {
        key: 'name',
        title: this.t.instant('pages.guardrails.fields.name'),
        width: '20%',
        sortable: true,
        component: AlTableTextComponent,
        // The origin marker rides in the name cell instead of its own column:
        // on the organisation page every row is an organisation rule, so a
        // dedicated column would be empty there.
        componentInputs: ({ value, row }) => ({
          text: value,
          max: 120,
          suffix: this.isInherited(row) ? inheritedLabel : undefined,
          suffixTooltip: this.isInherited(row)
            ? this.t.instant('pages.guardrails.inheritedTooltip')
            : undefined,
        }),
      },
      {
        key: 'definition', kind: 'text',
        title: this.t.instant('pages.guardrails.fields.definition'),
        // Free text — cut it, or one long rule stretches every other column.
        // Full text stays reachable on hover and in the edit dialog.
        width: '26%',
        component: AlTableTextComponent,
        componentInputs: ({ value }) => ({ text: value, max: 200 }),
      },
      {
        key: 'applies_to_templates',
        title: this.t.instant('pages.guardrails.fields.appliesTo'),
        width: '14%',
        component: AlTableBadgeComponent,
        componentInputs: ({ row }) => ({
          label: this.templateLabel(row),
          variant: 'primary',
          tooltip: this.templateLabel(row),
        }),
      },
      {
        key: 'enforce',
        title: this.t.instant('pages.guardrails.fields.enforce'),
        width: '13%',
        sortable: true,
        component: AlTableBadgeComponent,
        componentInputs: ({ value }) => ({
          label: this.t.instant('pages.guardrails.enforce.' + value),
          variant: value === 'hard' ? 'primary' : 'muted',
        }),
      },
      {
        key: 'action',
        title: this.t.instant('pages.guardrails.fields.action'),
        width: '12%',
        sortable: true,
        component: AlTableBadgeComponent,
        componentInputs: ({ value }) => ({
          label: this.t.instant('pages.guardrails.action.' + value),
          variant:
            value === 'block'
              ? 'danger'
              : value === 'redirect'
                ? 'warning'
                : 'muted',
        }),
      },
      {
        key: 'enabled',
        title: this.t.instant('words.status'),
        width: '7%',
        sortable: true,
        component: AlTableBadgeComponent,
        componentInputs: ({ value }) => ({
          label: this.t.instant(
            value
              ? 'pages.guardrails.status.enabled'
              : 'pages.guardrails.status.disabled'
          ),
          variant: value ? 'success' : 'muted',
        }),
      },
      {
        key: 'hit_count', kind: 'number',
        title: this.t.instant('pages.guardrails.fields.hits'),
        width: '8%',
        sortable: true,
      },
    ];

    const inheritedTooltip = this.t.instant(
      'pages.guardrails.inheritedTooltip'
    );
    const blockIfInherited = (row: GuardrailRule) => this.isInherited(row);
    const inheritedOr = (fallback: string) => (row: GuardrailRule) =>
      this.isInherited(row) ? inheritedTooltip : fallback;

    this.rowActions = [
      {
        key: 'history',
        title: this.t.instant('pages.guardrails.viewHistory'),
        icon: 'eye-outline',
      },
      {
        key: 'edit',
        title: this.t.instant('buttons.edit'),
        icon: 'edit-outline',
        disabled: blockIfInherited,
        tooltip: inheritedOr(this.t.instant('buttons.edit')),
      },
      {
        // Neutral label on purpose: `AlTableRowAction.title` is static, so an
        // "Aktivieren"/"Deaktivieren" pair would read wrong for half the rows.
        // `isMultiAction` puts it into the selection bar as well.
        key: 'toggle',
        title: this.t.instant('pages.guardrails.toggleStatus'),
        icon: 'toggle-right-outline',
        isMultiAction: true,
        disabled: blockIfInherited,
        tooltip: inheritedOr(this.t.instant('pages.guardrails.toggleStatus')),
      },
      {
        key: 'delete',
        title: this.t.instant('buttons.delete'),
        icon: 'trash-outline',
        isMultiAction: true,
        disabled: blockIfInherited,
        tooltip: inheritedOr(this.t.instant('buttons.delete')),
      },
    ];

    this.globalActions = [
      {
        key: 'create',
        title: this.t.instant('pages.guardrails.addRule'),
        icon: 'plus-outline',
      },
    ];
  }

  private templateLabel(rule: GuardrailRule): string {
    if (!rule.applies_to_templates?.length) {
      return this.t.instant('pages.guardrails.fields.allTemplates');
    }
    // `enums.assistantTemplate` is the canonical template-name map used by the
    // tutor wizard and the agent list — no second copy here, or the names
    // drift apart the first time one of them is renamed.
    return rule.applies_to_templates
      .map((code) => this.t.instant('enums.assistantTemplate.' + code))
      .join(', ');
  }

  // ── Data ──

  load(): void {
    this.isLoading = true;
    const request$ = this.tutorId
      ? this.guardrailService.listBotRules(this.tutorId)
      : this.guardrailService.listClientRules();
    request$.subscribe({
      next: (result) => {
        this.rules = result.data;
        this.total = result.total;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.toastrService.danger(
          this.t.instant('pages.guardrails.loadFailed'),
          ''
        );
      },
    });
  }

  // ── Table events ──

  onTableEvent(payload: { event: string; data: any }): void {
    if (payload.event === 'globalAction') {
      this.openDialog();
      return;
    }
    if (payload.event === 'multiAction') {
      this.onMultiAction(payload.data?.action?.key, payload.data?.rows || []);
      return;
    }
    if (payload.event !== 'rowAction') return;

    const rule = payload.data?.row as GuardrailRule;
    if (!rule) return;

    switch (payload.data?.action?.key) {
      case 'history':
        this.openHistory(rule);
        break;
      case 'edit':
        this.openDialog(rule);
        break;
      case 'toggle':
        this.onToggleEnabled(rule);
        break;
      case 'delete':
        this.onDelete(rule);
        break;
    }
  }

  // ── Bulk actions ──

  /**
   * The shared table's selection bar has no per-row disabled state, so an
   * inherited organisation rule CAN be ticked here. Filter it out and say how
   * many were skipped — silently dropping rows the user selected would be
   * worse than refusing them out loud.
   */
  private onMultiAction(key: string, rows: GuardrailRule[]): void {
    const actionable = rows.filter((r) => !this.isInherited(r));
    const skipped = rows.length - actionable.length;

    if (!actionable.length) {
      this.toastrService.warning(
        this.t.instant('pages.guardrails.bulkOnlyInherited'),
        ''
      );
      return;
    }

    if (key === 'delete') {
      const ref = this.dialogService.open(ConfirmDialogComponent, {
        context: {
          title: 'pages.guardrails.bulkDeleteTitle',
          message: this.t.instant('pages.guardrails.bulkDeleteMessage', {
            count: actionable.length,
          }),
          onConfirm: () => {
            this.runBulk(
              actionable.map((r) => this.guardrailService.deleteRule(r.id)),
              'pages.guardrails.bulkDeleted',
              actionable.length,
              skipped
            );
            ref.close();
          },
        },
      });
      return;
    }

    if (key === 'toggle') {
      // One target state for the whole selection instead of flipping each row
      // individually: after a mixed-state bulk toggle nobody can tell what the
      // result will be. If anything is still active, deactivate everything.
      const target = !actionable.some((r) => r.enabled);
      this.runBulk(
        actionable.map((r) =>
          this.guardrailService.updateRule(r.id, { enabled: target })
        ),
        'pages.guardrails.bulkUpdated',
        actionable.length,
        skipped
      );
    }
  }

  private runBulk(
    requests: Observable<unknown>[],
    successKey: string,
    count: number,
    skipped: number
  ): void {
    this.isLoading = true;
    forkJoin(requests).subscribe({
      next: () => {
        this.toastrService.success(
          this.t.instant(successKey, { count }),
          ''
        );
        if (skipped > 0) {
          this.toastrService.info(
            this.t.instant('pages.guardrails.bulkSkipped', { count: skipped }),
            ''
          );
        }
        this.load();
      },
      error: () => {
        this.isLoading = false;
        this.toastrService.danger(
          this.t.instant('pages.guardrails.bulkFailed'),
          ''
        );
        // Partial success is possible — reload so the table shows the truth.
        this.load();
      },
    });
  }

  // ── Actions ──

  openDialog(rule: GuardrailRule | null = null): void {
    if (rule && this.isInherited(rule)) return;
    this.dialogService.open(GuardrailRuleDialogComponent, {
      context: {
        rule,
        scope: this.scope,
        onConfirm: (input: GuardrailRuleInput) => {
          const request$ = rule
            ? this.guardrailService.updateRule(rule.id, input)
            : this.tutorId
              ? this.guardrailService.createBotRule(this.tutorId, input)
              : this.guardrailService.createClientRule(input);
          request$.subscribe({
            next: () => {
              this.toastrService.success(
                this.t.instant(
                  rule
                    ? 'pages.guardrails.updated'
                    : 'pages.guardrails.created'
                ),
                ''
              );
              this.load();
            },
            error: () =>
              this.toastrService.danger(
                this.t.instant('pages.guardrails.saveFailed'),
                ''
              ),
          });
        },
      },
    });
  }

  openHistory(rule: GuardrailRule): void {
    this.dialogService.open(GuardrailHitsDialogComponent, {
      context: { rule },
    });
  }

  onToggleEnabled(rule: GuardrailRule): void {
    if (this.isInherited(rule)) return;
    this.guardrailService
      .updateRule(rule.id, { enabled: !rule.enabled })
      .subscribe({
        next: () => this.load(),
        error: () =>
          this.toastrService.danger(
            this.t.instant('pages.guardrails.saveFailed'),
            ''
          ),
      });
  }

  onDelete(rule: GuardrailRule): void {
    if (this.isInherited(rule)) return;
    const ref = this.dialogService.open(ConfirmDialogComponent, {
      context: {
        title: 'pages.guardrails.deleteTitle',
        message: 'pages.guardrails.deleteMessage',
        onConfirm: () => {
          this.guardrailService.deleteRule(rule.id).subscribe({
            next: () => {
              this.toastrService.success(
                this.t.instant('pages.guardrails.deleted'),
                ''
              );
              this.load();
              ref.close();
            },
            error: () =>
              this.toastrService.danger(
                this.t.instant('pages.guardrails.deleteFailed'),
                ''
              ),
          });
        },
      },
    });
  }
}
